import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { ConfigProvider } from 'antd';
import viVN from 'antd/locale/vi_VN';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import ProtectedRoute from './components/ProtectedRoute';
import MainLayout from './layouts/MainLayout';
import MedicalFacilityLayout from './layouts/MedicalFacilityLayout';
import LoginPage from './pages/facility/LoginPage';
import AdminLoginPage from './pages/admin/AdminLoginPage';
// Admin pages (in facility folder)
import DashboardPage from './pages/facility/DashboardPage';
import PrescriptionManagementPage from './pages/facility/PrescriptionManagementPage';
import AddPrescriptionPage from './pages/facility/AddPrescriptionPage';
import ImportPrescriptionPage from './pages/facility/ImportPrescriptionPage';
import NationalPrescriptionPage from './pages/facility/NationalPrescriptionPage';
import InvoiceManagementPage from './pages/facility/InvoiceManagementPage';
import ClinicConfigPage from './pages/facility/ClinicConfigPage';
import APIConfigPage from './pages/facility/APIConfigPage';
import DoctorListPage from './pages/facility/DoctorListPage';
// User pages (admin_medical_facility - outside facility folder)
import FacilityDashboardPage from './pages/admin/FacilityDashboardPage';
import MedicalFacilityManagementPage from './pages/admin/MedicalFacilityManagementPage';
import SupplierManagementPage from './pages/admin/SupplierManagementPage';
import FacilityStatisticsPage from './pages/admin/FacilityStatisticsPage';
import UserManagementPage from './pages/admin/UserManagementPage';
import NotFoundPage from './pages/NotFoundPage';
import { antdTheme } from './config/theme';
import './App.css';

dayjs.locale('vi');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      staleTime: 1000 * 60 * 60 * 24, // 24 hours
    },
  },
});

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider locale={viVN} theme={antdTheme}>
          <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/admin/login" element={<AdminLoginPage />} />
            {/* Admin routes - use pages in facility folder */}
            <Route
              path="/facility"
              element={
                <ProtectedRoute allowedRoles={['admin_medical_facility']}>
                  <MedicalFacilityLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/facility/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="prescriptions" element={<PrescriptionManagementPage />} />
              <Route path="prescriptions/add" element={<AddPrescriptionPage />} />
              <Route path="prescriptions/import" element={<ImportPrescriptionPage />} />
              <Route path="national-prescriptions" element={<NationalPrescriptionPage />} />
              <Route path="invoices" element={<InvoiceManagementPage />} />
              <Route path="statistics" element={<div>Thống kê</div>} />
              <Route path="config/clinic" element={<ClinicConfigPage />} />
              <Route path="config/api" element={<APIConfigPage />} />
              <Route path="config/doctors" element={<DoctorListPage />} />
            </Route>
            {/* Admin medical facility routes - use pages outside facility folder */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute  allowedRoles={['admin']}>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<FacilityDashboardPage />} />
              <Route path="medical-facilities" element={<MedicalFacilityManagementPage />} />
              <Route path="suppliers" element={<SupplierManagementPage />} />
              <Route path="statistics" element={<FacilityStatisticsPage />} />
              <Route path="users" element={<UserManagementPage />} />
            </Route>
            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            {/* 404 - Catch all unmatched routes */}
            <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </BrowserRouter>
        </ConfigProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
